/**
 * LanguageContext — App-wide internationalization (i18n) system
 *
 * This module is a backward-compatible bridge over react-i18next.
 * All existing `useTranslation()` call-sites continue to work unchanged.
 *
 * Translations live in  public/locales/{lng}/translation.json  and are
 * loaded at runtime by i18next-http-backend.
 *
 * Usage:
 *   const { t } = useTranslation();
 *   <Button>{t('common.save')}</Button>
 *
 * Interpolation:
 *   t('org.settings.messages.settingUpdated', { field: 'Language' })
 *   // → "Language updated"
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';
import i18n, { type LanguageCode, isRtl, SUPPORTED_LANGUAGES } from '@/i18n';

// Re-export types for consumers that import from here
export type { LanguageCode } from '@/i18n';

// ─── Language label → BCP 47 code mapping ────────────────────────────────────

export const LANGUAGE_LABEL_TO_CODE: Record<string, LanguageCode> = {
  'English (US)': 'en',
  'English (UK)': 'en',
  'Spanish': 'es',
  'French': 'fr',
  'German': 'de',
  'Arabic': 'ar',
  'Hindi': 'hi',
  'Chinese (Simplified)': 'zh',
  'Japanese': 'ja',
  'Portuguese': 'pt',
  'Turkish': 'tr',
  'Korean': 'ko',
  'Russian': 'ru',
  'Italian': 'it',
  'Indonesian': 'id',
  'Vietnamese': 'vi',
  'Thai': 'th',
};

/**
 * Maps UI language labels to app `LanguageCode`.
 * Supports legacy labels, country-qualified options from `master_countries`
 * (e.g. "English (USA)" → en), and base language names (e.g. "German" → de).
 */
export function languageLabelToCode(label: string): LanguageCode {
  const trimmed = label.trim();
  if (LANGUAGE_LABEL_TO_CODE[trimmed]) return LANGUAGE_LABEL_TO_CODE[trimmed];

  const withoutRegion = trimmed.replace(/\s*\([^)]+\)\s*$/, '').trim();
  if (LANGUAGE_LABEL_TO_CODE[withoutRegion]) return LANGUAGE_LABEL_TO_CODE[withoutRegion];

  const SIMPLE_BASE: Record<string, LanguageCode> = {
    English: 'en',
    German: 'de',
    Spanish: 'es',
    French: 'fr',
    Arabic: 'ar',
    Hindi: 'hi',
    Chinese: 'zh',
    Japanese: 'ja',
    Portuguese: 'pt',
    Turkish: 'tr',
    Korean: 'ko',
    Russian: 'ru',
    Italian: 'it',
    Indonesian: 'id',
    Vietnamese: 'vi',
    Thai: 'th',
    Malay: 'id',
  };
  return SIMPLE_BASE[withoutRegion] ?? 'en';
}

// ─── Context (kept for backward compat, delegates to i18next) ────────────────

interface LanguageContextValue {
  languageCode: LanguageCode;
  setLanguageCode: (code: LanguageCode) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { t: i18nT, i18n: i18nInstance } = useI18nextTranslation();
  const languageCode = (i18nInstance.language || 'en') as LanguageCode;

  const handleSetLanguageCode = useCallback((code: LanguageCode) => {
    if (i18nInstance.language === code) return;
    i18nInstance.changeLanguage(code);
    localStorage.setItem('bms-language', code);
  }, [i18nInstance]);

  useEffect(() => {
    const lang = i18nInstance.language || 'en';
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
  }, [i18nInstance.language]);

  const t = useCallback(
    (key: string, vars?: Record<string, string>): string =>
      i18nT(key, vars as Record<string, unknown>) as string,
    [i18nT],
  );

  const value = useMemo(
    () => ({ languageCode, setLanguageCode: handleSetLanguageCode, t }),
    [languageCode, handleSetLanguageCode, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Returns `{ t, languageCode }` for translating UI strings.
 * Must be used inside <LanguageProvider>.
 */
export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Outside provider — use react-i18next directly as fallback
    return {
      languageCode: (i18n.language || 'en') as LanguageCode,
      t: (key: string, vars?: Record<string, string>) =>
        i18n.t(key, vars as Record<string, unknown>) as string,
    };
  }
  return { t: ctx.t, languageCode: ctx.languageCode };
}

/** Returns the language setter, used by OrgSettingsContext to sync org language. */
export function useSetLanguage() {
  const ctx = useContext(LanguageContext);
  return ctx?.setLanguageCode ?? ((code: LanguageCode) => {
    i18n.changeLanguage(code);
    localStorage.setItem('bms-language', code);
  });
}

// Re-exports for convenience
export { SUPPORTED_LANGUAGES, isRtl } from '@/i18n';
